/*
 * IRONiNK cart sync
 * -----------------
 * The product-options app charges some add-ons (Tribute Sign flag colour +$10, Marine Corps custom text +$25,
 * CDCR custom top ribbon +$30) through a hidden helper product called "__Customized-Items" priced at $1.
 * Each add-on adds one helper line whose quantity is the add-on price times the quantity of the product it
 * belongs to. The app tags both lines with the same __custom_item_rand_id and records the per-item price in
 * __custom_item_price.
 *
 * Shopify doesn't keep those two lines in step. Adding 3 signs from the product page is fine, but changing the
 * quantity in the cart afterwards leaves the helper line at its old size, so the add-on is under- (or over-)
 * charged. This script puts the helper lines back in step every time the cart page is opened or changed.
 *
 * It runs on every page that has a cart, after every cart change, and again when someone presses Check out, so
 * the charge is right before payment. (Native variants make all of this unnecessary: Shopify then prices the add-on
 * itself. See pricing-tests/docs/MODERNIZATION-STAGE1.md.)
 *
 * The decision logic (planHelperSync) is a plain function, exported for the Node tests in pricing-tests/.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.IronInkCartSync = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var HELPER_HANDLE = '__customized-items';

  function num(v) {
    var n = Number(v);
    return isFinite(n) ? n : 0;
  }
  function isHelper(item) {
    return item.handle === HELPER_HANDLE || /^__customized-items$/i.test(String(item.product_title || '').replace(/\s+/g, '-'));
  }

  /**
   * Given Shopify's cart JSON, say what the helper lines should look like.
   * Returns { updates: { <line key>: <quantity> }, adds: [ { randId, perItem, quantity } ], changes: [ ... ] }
   * - updates: existing helper lines whose quantity is wrong (0 removes a helper whose product is gone)
   * - adds:    add-ons that have no helper line at all
   * Everything is empty when the cart is already in step.
   */
  function planHelperSync(cart) {
    var items = (cart && cart.items) || [];
    var needed = {};
    var perItemOf = {};
    var i, it, id;
    for (i = 0; i < items.length; i++) {
      it = items[i];
      if (isHelper(it)) continue;
      var props = it.properties || {};
      id = props.__custom_item_rand_id;
      var perItem = num(props.__custom_item_price);
      if (!id || perItem <= 0) continue;
      needed[id] = (needed[id] || 0) + perItem * it.quantity;
      perItemOf[id] = perItem;
    }
    var updates = {};
    var changes = [];
    var seen = {};
    for (i = 0; i < items.length; i++) {
      it = items[i];
      if (!isHelper(it)) continue;
      id = (it.properties || {}).__custom_item_rand_id;
      var want = id && needed[id] ? needed[id] : 0; // no product left for this add-on -> remove it
      if (id) seen[id] = true;
      if (it.quantity !== want) {
        updates[it.key] = want;
        changes.push({ key: it.key, from: it.quantity, to: want });
      }
    }
    var adds = [];
    Object.keys(needed).forEach(function (rid) {
      if (!seen[rid]) {
        adds.push({ randId: rid, perItem: perItemOf[rid], quantity: needed[rid] });
        changes.push({ key: 'missing:' + rid, from: 0, to: needed[rid] });
      }
    });
    return { updates: updates, adds: adds, changes: changes };
  }

  var api = { planHelperSync: planHelperSync, isHelper: isHelper };

  // ---------- page wiring (browser only) ----------
  if (typeof document === 'undefined') return api;

  var busy = false;
  var timer = null;
  var onCartPage = /^\/cart\/?$/.test(window.location.pathname);

  function reloadGuardAllows() {
    // Never reload more than twice in ten seconds, whatever happens.
    try {
      var now = Date.now();
      var log = JSON.parse(sessionStorage.getItem('iwCartSync') || '[]').filter(function (t) { return now - t < 10000; });
      if (log.length >= 2) return false;
      log.push(now);
      sessionStorage.setItem('iwCartSync', JSON.stringify(log));
    } catch (e) { /* storage unavailable: allow */ }
    return true;
  }

  function applyPlan(plan) {
    var steps = [];
    if (Object.keys(plan.updates).length) {
      steps.push(function () {
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: plan.updates })
        });
      });
    }
    if (plan.adds.length) {
      steps.push(function () {
        return fetch('/products/' + HELPER_HANDLE + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (helper) {
            var variantId = helper.variants[0].id;
            return fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: plan.adds.map(function (a) {
                  return { id: variantId, quantity: a.quantity, properties: { __custom_item_rand_id: a.randId, __custom_item_price: String(a.perItem) } };
                })
              })
            });
          });
      });
    }
    return steps.reduce(function (p, step) { return p.then(step); }, Promise.resolve());
  }

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // Put the helper lines right. Resolves true when something was changed.
  function syncNow() {
    return readCart().then(function (cart) {
      var plan = planHelperSync(cart);
      if (!plan.changes.length) return false;
      return applyPlan(plan).then(function () { return true; });
    });
  }

  function run() {
    if (busy) return;
    busy = true;
    syncNow()
      .then(function (changed) {
        busy = false;
        // Only the cart page shows the quantities, so only there is a refresh worth doing.
        if (changed && onCartPage && reloadGuardAllows()) window.location.reload();
      })
      .catch(function () { busy = false; });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, 500);
  }

  // Re-check after every cart change made by the theme (quantity buttons, remove links, add to cart),
  // on every page, so the charge is right as soon as the cart changes - not only when the cart page opens.
  var realFetch = window.fetch;
  if (typeof realFetch === 'function') {
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = realFetch.apply(this, arguments);
      if (!busy && /\/cart\/(change|update|add)(\.js)?(\?|$)/.test(url)) {
        promise.then(schedule, function () {});
      }
      return promise;
    };
  }

  // Check out only after the cart has been verified. Whoever presses Check out right after changing a
  // quantity (before the check above has run) is stopped for a moment, the charge is corrected, and
  // checkout then continues by itself. If the check can't run, checkout is never blocked.
  function checkoutControl(el) {
    return el && el.closest ? el.closest('button[name="checkout"], input[name="checkout"], a[href^="/checkout"]') : null;
  }
  document.addEventListener('click', function (e) {
    var ctl = checkoutControl(e.target);
    if (!ctl || ctl.iwCartVerified) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var proceed = function () {
      ctl.iwCartVerified = true;
      if (ctl.tagName === 'A') { window.location.href = ctl.href; return; }
      ctl.click();
      ctl.iwCartVerified = false;
    };
    syncNow().then(proceed, proceed);
  }, true);

  function hasCartCookie() { return /(?:^|;\s*)cart=/.test(document.cookie); }
  function start() { if (onCartPage || hasCartCookie()) run(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  window.addEventListener('pageshow', function () { if (onCartPage || hasCartCookie()) schedule(); });

  api.syncNow = syncNow;
  return api;
});
